import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchScopedCentres, fetchVisitAudits, updateVisitAudit } from 'api'
import {
  ListPagination,
  usePagedRows
} from 'app/components/generic/ListPagination'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

import { ANSWERS, AuditQuestions } from './AuditQuestions'

const ALL_CENTRES = 'all'
const niceDate = (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—')

// "9 of 11" = Yes out of the questions answered Yes or No; Not sure is left out.
const sectionScore = (audit, section) => {
  const given = section.questions
    .map((q) => audit.answers?.[q.key])
    .filter((a) => a === 'yes' || a === 'no')
  if (!given.length) return '—'
  return `${given.filter((a) => a === 'yes').length} of ${given.length}`
}

const VisitAudits = ({ kind, formKind, title, staffFields = false }) => {
  const [records, setRecords] = useState([])
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(null) // the audit being edited; null = closed
  const [viewing, setViewing] = useState(null) // null = pop-up closed
  const [saving, setSaving] = useState(false)
  const [centres, setCentres] = useState([])
  const [unrestricted, setUnrestricted] = useState(false)
  const [listCentre, setListCentre] = useState(ALL_CENTRES)
  // A new audit is filed in its own tab: reload when the person comes back.
  const formTabOpened = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchVisitAudits(kind)
      setRecords(data.results || [])
      setSections(data.sections || [])
    } catch (error) {
      showToast(`Could not load ${title} audits`, constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [kind, title])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const onFocus = () => {
      if (!formTabOpened.current) return
      formTabOpened.current = false
      load()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [load])

  useEffect(() => {
    let active = true
    fetchScopedCentres()
      .then((data) => {
        if (!active) return
        setCentres((data && data.centres) || [])
        setUnrestricted(Boolean(data && data.unrestricted))
      })
      .catch(() => showToast('Could not load centres', constants.ERROR))
    return () => {
      active = false
    }
  }, [])

  const inCentre = useMemo(
    () =>
      listCentre === ALL_CENTRES
        ? records
        : records.filter((r) => r.centre === listCentre),
    [records, listCentre]
  )
  const search = useMemo(
    () => ({
      date: (r) => niceDate(r.audit_date),
      centre: (r) => r.centre_name,
      observer: (r) => r.observer,
      ...(staffFields
        ? { staff: (r) => r.staff_name, grade: (r) => r.grade }
        : {})
    }),
    [staffFields]
  )
  const colSearch = useColumnSearch(inCentre, search)
  const { pageRows, paginationProps, resetPage } = usePagedRows(colSearch.rows)
  useEffect(() => {
    resetPage()
  }, [listCentre, colSearch.search])

  // The centre comes from this page's Centre filter, so the form has no picker.
  const openAdd = () => {
    const centre =
      listCentre !== ALL_CENTRES
        ? listCentre
        : centres.length === 1
        ? centres[0].uuid
        : ''
    if (!centre) {
      showToast('Pick a centre first', constants.ERROR)
      return
    }
    formTabOpened.current = true
    window.open(`/audit-form/${formKind}/${centre}`, '_blank', 'noopener')
  }

  const openEdit = (audit) => {
    setViewing(null)
    setForm({ ...audit })
  }

  const setField = (key) => (e) => {
    const { value } = e.target
    setForm((f) => ({ ...f, [key]: value }))
  }

  const setAnswer = (key) => (e) => {
    const { value } = e.target
    setForm((f) => ({ ...f, answers: { ...f.answers, [key]: value } }))
  }

  const setSectionComment = (key) => (e) => {
    const { value } = e.target
    setForm((f) => ({ ...f, comments: { ...f.comments, [key]: value } }))
  }

  const unanswered = (f) =>
    sections.flatMap((s) => s.questions).filter((q) => !f.answers?.[q.key])
      .length

  const save = async () => {
    if (
      !form.audit_date ||
      !form.observer.trim() ||
      (staffFields && !form.staff_name.trim())
    ) {
      showToast(
        staffFields
          ? 'Date, Observer and Housekeeping staff name are required'
          : 'Date and Observer are required',
        constants.ERROR
      )
      return
    }
    const left = unanswered(form)
    if (left) {
      showToast(
        `${left} question${left > 1 ? 's' : ''} still to answer`,
        constants.ERROR
      )
      return
    }
    const payload = {
      audit_date: form.audit_date,
      observer: form.observer,
      answers: form.answers,
      comments: form.comments,
      ...(staffFields ? { staff_name: form.staff_name, grade: form.grade } : {})
    }
    setSaving(true)
    try {
      await updateVisitAudit(kind, form.uuid, payload)
      setForm(null)
      showToast('Audit saved', constants.SUCCESS)
      load()
    } catch (error) {
      const detail = error && error.response && error.response.data
      showToast(
        (detail && (detail.detail || JSON.stringify(detail))) ||
          'Could not save the audit',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  const colCount = 5 + (staffFields ? 2 : sections.length - 1)

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5">{title}</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 220 }}
          >
            <InputLabel>Centre</InputLabel>
            <Select
              label="Centre"
              value={listCentre}
              onChange={(e) => setListCentre(e.target.value)}
            >
              <MenuItem value={ALL_CENTRES}>
                {unrestricted ? 'All centres' : 'All my centres'}
              </MenuItem>
              {centres.map((c) => (
                <MenuItem key={c.uuid} value={c.uuid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={openAdd}>
            + New audit
          </Button>
        </Box>
      </Box>

      {/* No scroll box around the table: the heading sticks to the top of the
          window, and the table fits the window width. */}
      <Table size="small" style={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <SearchHeadCell label="Date" search={colSearch.box('date')} />
            <SearchHeadCell label="Centre" search={colSearch.box('centre')} />
            <SearchHeadCell
              label="Observer"
              search={colSearch.box('observer')}
            />
            {staffFields && (
              <>
                <SearchHeadCell
                  label="Housekeeping staff"
                  search={colSearch.box('staff')}
                />
                <SearchHeadCell label="Grade" search={colSearch.box('grade')} />
              </>
            )}
            {/* HK has one section, so its one score sits in a single column. */}
            {sections.map((s) => (
              <SearchHeadCell
                key={s.key}
                label={staffFields ? 'Yes' : s.title}
              />
            ))}
            <SearchHeadCell label="Details" align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={colCount} align="center">
                <CircularProgress size={22} />
              </TableCell>
            </TableRow>
          )}
          {!loading && !colSearch.rows.length && (
            <TableRow>
              <TableCell colSpan={colCount} align="center">
                {records.length ? 'No audits match.' : 'No audits filed yet.'}
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            pageRows.map((r) => (
              <TableRow key={r.uuid} hover>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  {niceDate(r.audit_date)}
                </TableCell>
                <TableCell>{r.centre_name}</TableCell>
                <TableCell>{r.observer}</TableCell>
                {staffFields && (
                  <>
                    <TableCell>{r.staff_name}</TableCell>
                    <TableCell>{r.grade || '—'}</TableCell>
                  </>
                )}
                {sections.map((s) => (
                  <TableCell key={s.key} style={{ whiteSpace: 'nowrap' }}>
                    {sectionScore(r, s)}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Button size="small" onClick={() => setViewing(r)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <ListPagination {...paginationProps} />

      {/* Edit form. A new audit is filed on its own page: Public/AuditForm.js */}
      <Dialog
        open={!!form}
        onClose={() => setForm(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {form && `Edit ${title} · ${form.centre_name}`}
        </DialogTitle>
        <DialogContent>
          {form && (
            <Box
              display="flex"
              flexDirection="column"
              style={{ gap: 12 }}
              pt={1}
            >
              <TextField
                type="date"
                label="Date"
                required
                value={form.audit_date}
                onChange={setField('audit_date')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Observer"
                required
                value={form.observer}
                onChange={setField('observer')}
              />
              {staffFields && (
                <>
                  <TextField
                    label="Housekeeping staff name"
                    required
                    helperText="Write All if you observed the whole housekeeping team"
                    value={form.staff_name}
                    onChange={setField('staff_name')}
                  />
                  <TextField
                    label="Grade"
                    value={form.grade}
                    onChange={setField('grade')}
                  />
                </>
              )}
              <AuditQuestions
                sections={sections}
                answers={form.answers}
                comments={form.comments}
                sectionComments={!staffFields}
                onAnswer={setAnswer}
                onSectionComment={setSectionComment}
              />
              {staffFields && (
                <TextField
                  label="Comments"
                  multiline
                  minRows={2}
                  value={form.comments || ''}
                  onChange={setField('comments')}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read-only pop-up */}
      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {viewing &&
            `${title} · ${viewing.centre_name} · ${niceDate(
              viewing.audit_date
            )}`}
        </DialogTitle>
        <DialogContent>
          {viewing && (
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              <Typography variant="body2">
                <strong>Observer:</strong> {viewing.observer}
              </Typography>
              {staffFields && (
                <Typography variant="body2">
                  <strong>Housekeeping staff:</strong> {viewing.staff_name}
                  {viewing.grade ? ` · ${viewing.grade}` : ''}
                </Typography>
              )}
              {sections.map((s) => (
                <Box key={s.key} mt={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    {s.title}
                  </Typography>
                  {s.questions.map((q) => {
                    const answer = ANSWERS.find(
                      (a) => a.value === viewing.answers?.[q.key]
                    )
                    return (
                      <Box
                        key={q.key}
                        display="flex"
                        justifyContent="space-between"
                        style={{ gap: 12 }}
                      >
                        <Typography variant="body2" style={{ flex: 1 }}>
                          {q.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 600,
                            color: answer ? answer.color : '#9a8f82'
                          }}
                        >
                          {answer ? answer.label : '—'}
                        </Typography>
                      </Box>
                    )
                  })}
                  {!staffFields && viewing.comments?.[s.key] && (
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      <strong>Comment:</strong> {viewing.comments[s.key]}
                    </Typography>
                  )}
                </Box>
              ))}
              {staffFields && viewing.comments && (
                <Typography variant="body2">
                  <strong>Comments:</strong> {viewing.comments}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openEdit(viewing)}>Edit</Button>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VisitAudits
